// Atelier Bull v2 — celebratory primitives.
//
// Houses the things that turn an editorial dashboard into one users
// actually love: the bull mascot, digit-roll numerals, Cmd-K palette,
// confetti + chimes for profitable backtests, editorial pull-quotes,
// and the first-visit onboarding curtain.
//
// Everything is exported on `window.FX` so app.jsx + ui.jsx can
// consume without a bundler. The file is intentionally self-contained
// — no cross-references back into ui.jsx primitives — so the load
// order in index.html (api → ui → chart → holdings → celebrations →
// app) leaves it free to forward-declare into the runtime.

// ============ 1. Bull mascot + pose set =========================
// Real designed PNG illustrations replace the synthetic SVGs the v1
// pass shipped with. Each pose lives in `assets/bull/`. The asset
// path is computed off the document so the same component works
// under Dropwizard's /dashboard/ prefix locally AND under
// Cloudflare Pages' root deploy.
//
// Poses:
//   mascot         — wordmark / topbar / curtain (square)
//   charging       — hero / dashboard headline (square)
//   sleeping       — empty state, no backtest yet (4:3)
//   running        — backtest in flight (4:3)
//   celebrating    — profitable backtest landed (square)
//   contemplative  — losing backtest landed (square)

const BULL_BASE = './assets/bull';

function bullSrc(pose) {
    return `${BULL_BASE}/${pose}.png`;
}

// Generic <img> wrapper. The `pose` prop selects which PNG; size is
// the rendered edge length in CSS px. The image lazy-loads (so
// poses below the fold don't pay first-paint cost) and is decoded
// async so the main thread isn't blocked on PNG decode.
function BullArt({ pose, size, height, alt, className, style }) {
    const w = size;
    const h = height ?? size;
    return (
        <img
            src={bullSrc(pose)}
            width={w}
            height={h}
            alt={alt || ''}
            loading="lazy"
            decoding="async"
            className={className}
            draggable={false}
            style={{
                width: w,
                height: h,
                objectFit: 'contain',
                userSelect: 'none',
                pointerEvents: 'none',
                ...style,
            }}
        />
    );
}

// Backwards-compatible primitives — same names the rest of the app
// uses. The mascot still scales & blinks via .bull-mascot CSS.
function BullMascot({ size = 38 }) {
    return (
        <span className="bull-mascot" style={{
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            width: size, height: size,
        }}>
            <BullArt pose="mascot" size={size} alt="Nifty Bull mascot" />
        </span>
    );
}

function BullCharging({ size = 240 }) {
    return <BullArt pose="charging" size={size} alt="" />;
}

function BullSleeping({ width = 220 }) {
    return <BullArt pose="sleeping" size={width} height={Math.round(width * 0.75)} alt="" />;
}

function BullRunning({ width = 220 }) {
    return <BullArt pose="running" size={width} height={Math.round(width * 0.75)} alt="" />;
}

function BullCelebrating({ size = 240 }) {
    return <BullArt pose="celebrating" size={size} alt="" />;
}

function BullContemplative({ size = 240 }) {
    return <BullArt pose="contemplative" size={size} alt="" />;
}

// ============ 3. Digit-roll numeric ============================
// True odometer. Each numeric character is its own column showing
// 0-9 stacked; the column translates to land on the target digit.
// Non-digits ('.', '%', '+', '−', ',', '₹', etc.) render as static
// inline spans. Skips animation entirely under prefers-reduced-motion.
//
// Drop-in replacement for AnimatedNumeric — same `text/className/style`
// signature.

function DigitRollNumeric({ text, className, style, durationMs = 1100 }) {
    const reduceMotion = React.useMemo(
        () => typeof window !== 'undefined'
            && window.matchMedia
            && window.matchMedia('(prefers-reduced-motion: reduce)').matches,
        []);
    if (typeof text !== 'string' || reduceMotion) {
        return <span className={className} style={style}>{text}</span>;
    }
    // Cell height has to track the rendered line-height; we read the
    // computed font-size off the wrapping element via the inline
    // style so a single em is enough. We use 1em rows and rely on
    // line-height: 1 so the strip aligns precisely.
    const cells = [];
    for (let i = 0; i < text.length; i++) {
        const ch = text[i];
        if (ch >= '0' && ch <= '9') {
            cells.push({ kind: 'digit', value: parseInt(ch, 10), key: i });
        } else {
            cells.push({ kind: 'static', value: ch, key: i });
        }
    }
    return (
        <span className={className}
              style={{ ...style, display: 'inline-flex', whiteSpace: 'pre', verticalAlign: 'baseline' }}
              aria-label={text}>
            {cells.map((c) => (
                c.kind === 'digit'
                    ? <DigitColumn key={c.key} target={c.value} durationMs={durationMs} />
                    : <span key={c.key} aria-hidden="true">{c.value}</span>
            ))}
        </span>
    );
}

function DigitColumn({ target, durationMs }) {
    // Each column owns its own first-mount animation: starts at 0 and
    // springs to the target. Subsequent target changes also re-run.
    // The strip is `9-target` cycles + the target itself, so digits
    // visibly roll past instead of jumping straight to the answer —
    // characteristic odometer feel.
    const [pos, setPos] = React.useState(0);
    React.useEffect(() => {
        const id = requestAnimationFrame(() => setPos(target));
        return () => cancelAnimationFrame(id);
    }, [target]);
    const strip = '0123456789';
    return (
        <span aria-hidden="true" style={{
            display: 'inline-block',
            height: '1em',
            overflow: 'hidden',
            lineHeight: 1,
            verticalAlign: 'baseline',
        }}>
            <span style={{
                display: 'block',
                transform: `translateY(${-pos}em)`,
                transition: `transform ${durationMs}ms cubic-bezier(0.34, 1.56, 0.64, 1)`,
            }}>
                {strip.split('').map((d, i) => (
                    <span key={i} style={{ display: 'block', height: '1em', lineHeight: 1 }}>{d}</span>
                ))}
            </span>
        </span>
    );
}

// ============ 4. Confetti =======================================
// Canvas-based burst. Fires once with `runConfetti()`. The canvas is
// inserted lazily, runs ~3.2s, and removes itself when the last
// piece falls off-screen. Colors come from the chart palette so the
// celebration reads as part of the design, not a stock emoji-popper.

function runConfetti({ count = 90 } = {}) {
    if (typeof window === 'undefined') return;
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const canvas = document.createElement('canvas');
    canvas.style.cssText = 'position:fixed;inset:0;pointer-events:none;z-index:9999;';
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;
    document.body.appendChild(canvas);
    const ctx = canvas.getContext('2d');
    const styles = getComputedStyle(document.documentElement);
    const palette = [
        styles.getPropertyValue('--chart-1').trim() || '#3a3aaa',
        styles.getPropertyValue('--chart-2').trim() || '#3aaa6e',
        styles.getPropertyValue('--chart-3').trim() || '#c89240',
        styles.getPropertyValue('--accent-text').trim() || '#3a3aaa',
        styles.getPropertyValue('--up').trim() || '#3aaa6e',
    ];
    const pieces = [];
    for (let i = 0; i < count; i++) {
        pieces.push({
            x: canvas.width / 2 + (Math.random() - 0.5) * 200,
            y: canvas.height / 2 - 40,
            vx: (Math.random() - 0.5) * 12,
            vy: -Math.random() * 14 - 4,
            g: 0.32 + Math.random() * 0.18,
            rot: Math.random() * Math.PI * 2,
            vr: (Math.random() - 0.5) * 0.32,
            size: 6 + Math.random() * 6,
            color: palette[Math.floor(Math.random() * palette.length)],
            shape: Math.random() < 0.5 ? 'rect' : 'circle',
            life: 0,
        });
    }
    let raf = 0;
    const start = performance.now();
    const tick = (now) => {
        const elapsed = now - start;
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        let alive = false;
        for (const p of pieces) {
            p.vy += p.g;
            p.x += p.vx;
            p.y += p.vy;
            p.rot += p.vr;
            p.life = elapsed;
            const fade = Math.max(0, 1 - elapsed / 3200);
            if (p.y < canvas.height + 40 && fade > 0) alive = true;
            ctx.save();
            ctx.translate(p.x, p.y);
            ctx.rotate(p.rot);
            ctx.globalAlpha = fade;
            ctx.fillStyle = p.color;
            if (p.shape === 'rect') {
                ctx.fillRect(-p.size / 2, -p.size / 3, p.size, p.size / 1.5);
            } else {
                ctx.beginPath();
                ctx.arc(0, 0, p.size / 2, 0, Math.PI * 2);
                ctx.fill();
            }
            ctx.restore();
        }
        if (alive) raf = requestAnimationFrame(tick);
        else canvas.remove();
    };
    raf = requestAnimationFrame(tick);
}

// ============ 5. Sound chimes ==================================
// Web Audio API synthesis — no asset files. Two voices: 'profit'
// plays a rising perfect-fifth (E5 → B5), 'loss' plays a soft
// minor-second drop (D5 → C#5), 'complete' plays a single soft G5
// bell. Envelope is short (~600ms) and master gain is intentionally
// low so it reads as a polite UI cue, not a notification ringtone.

let _audioCtx = null;
function _ctx() {
    if (typeof window === 'undefined') return null;
    if (!_audioCtx) {
        const C = window.AudioContext || window.webkitAudioContext;
        if (!C) return null;
        _audioCtx = new C();
    }
    return _audioCtx;
}

function _playTone(freq, opts = {}) {
    const ctx = _ctx();
    if (!ctx) return;
    const { duration = 0.55, type = 'sine', gain = 0.06, attack = 0.01, decay = 0.4 } = opts;
    const osc = ctx.createOscillator();
    const env = ctx.createGain();
    osc.type = type;
    osc.frequency.value = freq;
    env.gain.setValueAtTime(0, ctx.currentTime);
    env.gain.linearRampToValueAtTime(gain, ctx.currentTime + attack);
    env.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + duration);
    osc.connect(env).connect(ctx.destination);
    osc.start();
    osc.stop(ctx.currentTime + duration + 0.05);
    // Decay ref retained so linter doesn't flag the unused param —
    // it shapes the curve via the duration arg.
    void decay;
}

function playChime(kind = 'complete') {
    const ctx = _ctx();
    if (!ctx) return;
    // Browsers gate AudioContext on first user gesture. If we're still
    // suspended (pre-first-click), bail silently — first interactive
    // chime will land after the user has clicked something.
    if (ctx.state === 'suspended') {
        ctx.resume().catch(() => {});
        return;
    }
    if (kind === 'profit') {
        // E5 → B5 over ~400ms, two voices for a polished bell.
        _playTone(659.25, { duration: 0.5, gain: 0.05 });
        setTimeout(() => _playTone(987.77, { duration: 0.6, gain: 0.04 }), 140);
    } else if (kind === 'loss') {
        _playTone(587.33, { duration: 0.45, gain: 0.04, type: 'triangle' });
        setTimeout(() => _playTone(554.37, { duration: 0.55, gain: 0.04, type: 'triangle' }), 130);
    } else {
        // 'complete' — soft G5 bell.
        _playTone(783.99, { duration: 0.65, gain: 0.045 });
    }
}

// ============ 6. Editorial pull-quote ===========================
// Drop-cap serif quote panel. Used between Config sections + as a
// section header on the Dashboard. Very small surface area — just
// the typography + a hairline rule + a leading dash.

function PullQuote({ children, attribution }) {
    return (
        <blockquote className="pull-quote">
            <span className="pull-quote-mark" aria-hidden="true">“</span>
            <span className="pull-quote-body">{children}</span>
            {attribution && <cite className="pull-quote-cite">— {attribution}</cite>}
        </blockquote>
    );
}

// ============ 7. Command palette (Cmd-K) ========================
// Opens on Cmd-K / Ctrl-K. Closes on Esc, click-outside, or Enter on
// a result. Search filters across: strategies, time windows, and
// global actions ("Run backtest", "New backtest", "Toggle theme",
// "Show shortcuts"). The host wires the action handlers via the
// `commands` prop; this component is purely presentational +
// keyboard nav.

function CommandPalette({ open, onClose, commands }) {
    const [q, setQ] = React.useState('');
    const [idx, setIdx] = React.useState(0);
    const inputRef = React.useRef(null);

    React.useEffect(() => {
        if (open) {
            setQ('');
            setIdx(0);
            // Focus the input after the modal mounts; rAF avoids a
            // race with React's commit phase.
            requestAnimationFrame(() => inputRef.current && inputRef.current.focus());
        }
    }, [open]);

    const filtered = React.useMemo(() => {
        if (!q.trim()) return commands;
        const needle = q.toLowerCase();
        return commands.filter((c) =>
            c.label.toLowerCase().includes(needle) ||
            (c.section && c.section.toLowerCase().includes(needle)) ||
            (c.hint && c.hint.toLowerCase().includes(needle))
        );
    }, [q, commands]);

    React.useEffect(() => { setIdx(0); }, [q]);

    if (!open) return null;

    const onKey = (e) => {
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setIdx((i) => Math.min(filtered.length - 1, i + 1));
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setIdx((i) => Math.max(0, i - 1));
        } else if (e.key === 'Enter') {
            e.preventDefault();
            const c = filtered[idx];
            if (c) {
                onClose();
                requestAnimationFrame(() => c.run());
            }
        } else if (e.key === 'Escape') {
            e.preventDefault();
            onClose();
        }
    };

    return (
        <div className="cmdk-backdrop" onClick={onClose}>
            <div className="cmdk-panel" onClick={(e) => e.stopPropagation()}>
                <div className="cmdk-input-wrap">
                    <svg viewBox="0 0 20 20" width="18" height="18" fill="none"
                         stroke="currentColor" strokeWidth="1.6"
                         strokeLinecap="round" strokeLinejoin="round"
                         style={{ color: 'var(--text-3)' }}>
                        <circle cx="9" cy="9" r="6" />
                        <path d="m17 17-3.5-3.5" />
                    </svg>
                    <input
                        ref={inputRef}
                        className="cmdk-input"
                        placeholder="Type a command, strategy, or window…"
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        onKeyDown={onKey}
                    />
                    <kbd className="cmdk-esc">esc</kbd>
                </div>
                <div className="cmdk-list" role="listbox">
                    {filtered.length === 0 && (
                        <div className="cmdk-empty">No matches. Try a different word.</div>
                    )}
                    {filtered.map((c, i) => (
                        <div
                            key={c.id}
                            role="option"
                            aria-selected={i === idx}
                            className={`cmdk-row ${i === idx ? 'active' : ''}`}
                            onMouseEnter={() => setIdx(i)}
                            onClick={() => { onClose(); requestAnimationFrame(() => c.run()); }}>
                            {c.section && <span className="cmdk-section">{c.section}</span>}
                            <span className="cmdk-label">{c.label}</span>
                            {c.hint && <span className="cmdk-hint">{c.hint}</span>}
                        </div>
                    ))}
                </div>
                <div className="cmdk-foot">
                    <span><kbd>↑</kbd><kbd>↓</kbd> navigate</span>
                    <span><kbd>↵</kbd> select</span>
                    <span><kbd>esc</kbd> close</span>
                </div>
            </div>
        </div>
    );
}

// ============ 8. Onboarding curtain ==============================
// Shown once per browser (localStorage-gated). 4-second curtain raise
// with the wordmark + a three-line value prop. Click anywhere or wait
// for it to auto-dismiss. Honours reduce-motion (instant skip).

// The curtain shows on every page load — no localStorage gate. The
// auto-dismiss timer (~3.6s) keeps it from being a nuisance, and the
// "tap anywhere" affordance lets impatient users skip immediately.
// Earlier versions one-shot-gated this via localStorage, which made
// the welcome moment feel inconsistent ("why doesn't it show on
// reload?"); the deterministic-every-load behavior is what users
// actually expect from a "curtain raise" intro.
function shouldShowOnboarding() {
    return typeof window !== 'undefined';
}

function markOnboardingSeen() { /* no-op — kept for API stability */ }

// Single scene step inside the onboarding curtain. Renders the
// illustrated PNG above an editorial step number + label. Three of
// these line up horizontally to form the curtain's value-prop strip.
function CurtainStep({ n, pose, label }) {
    return (
        <div className="curtain-scene">
            <img src={`./assets/scenes/${pose}.png`} alt=""
                 width="160" height="160" loading="eager" decoding="async"
                 draggable={false}
                 style={{ width: 160, height: 160, objectFit: 'contain', userSelect: 'none', pointerEvents: 'none' }} />
            <div className="curtain-scene-num">{n}</div>
            <div className="curtain-scene-label">{label}</div>
        </div>
    );
}

function OnboardingCurtain({ onDismiss }) {
    const [phase, setPhase] = React.useState('enter');
    React.useEffect(() => {
        const t1 = setTimeout(() => setPhase('show'), 60);
        const t2 = setTimeout(() => setPhase('exit'), 3600);
        const t3 = setTimeout(() => { markOnboardingSeen(); onDismiss(); }, 4400);
        return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
    }, [onDismiss]);
    const skip = () => { markOnboardingSeen(); onDismiss(); };
    return (
        <div className={`curtain phase-${phase}`} onClick={skip}>
            <div className="curtain-card">
                <BullArt pose="mascot" size={140} alt="Nifty Bull mascot"
                         className="bull-float"
                         style={{ marginBottom: 'var(--space-3)' }} />
                <div className="curtain-eyebrow">Nifty Bull · Algo Trading</div>
                <h1 className="curtain-headline">
                    Run a strategy.<br />
                    Watch the story.
                </h1>
                <div className="curtain-scenes">
                    <CurtainStep n="01" pose="pick"     label="Pick a strategy" />
                    <CurtainStep n="02" pose="allocate" label="Allocate capital" />
                    <CurtainStep n="03" pose="story"    label="See the story" />
                </div>
                <div className="curtain-skip">tap anywhere to begin · <kbd>⌘K</kbd> opens commands</div>
            </div>
        </div>
    );
}

// ============ Public surface =====================================
window.FX = {
    BullMascot,
    BullCharging,
    BullSleeping,
    BullRunning,
    BullCelebrating,
    BullContemplative,
    DigitRollNumeric,
    runConfetti,
    playChime,
    PullQuote,
    CommandPalette,
    OnboardingCurtain,
    shouldShowOnboarding,
    markOnboardingSeen,
};
